import React, { useState } from 'react';
import { CheckCircle2, RotateCcw, XCircle, Loader2 } from 'lucide-react';
import { TaskStatus, TASK_STATUS_LABELS, TASK_STATUS_COLORS } from '../../../types/annotation';

interface ReviewPanelProps {
  taskId: string;
  currentStatus: TaskStatus;
  latestReview?: { status: string; comments?: string };
  onReview: (
    status: 'approved' | 'rejected' | 'revision_needed',
    comments: string,
  ) => Promise<void>;
}

type ReviewAction = 'approved' | 'rejected' | 'revision_needed';

interface PendingConfirm {
  action: ReviewAction;
}

const ACTION_CONFIG: Record<
  ReviewAction,
  { label: string; icon: React.ReactNode; buttonClass: string; confirmText: string }
> = {
  approved: {
    label: 'Approve',
    icon: <CheckCircle2 className="w-4 h-4" />,
    buttonClass:
      'bg-green-600 text-white hover:bg-green-700 focus:ring-green-400',
    confirmText: 'Are you sure you want to approve this task?',
  },
  revision_needed: {
    label: 'Needs Revision',
    icon: <RotateCcw className="w-4 h-4" />,
    buttonClass:
      'bg-orange-500 text-white hover:bg-orange-600 focus:ring-orange-400',
    confirmText: 'Mark this task as needing revision?',
  },
  rejected: {
    label: 'Reject',
    icon: <XCircle className="w-4 h-4" />,
    buttonClass:
      'bg-red-600 text-white hover:bg-red-700 focus:ring-red-400',
    confirmText: 'Are you sure you want to reject this task? This action cannot be undone.',
  },
};

const ReviewPanel: React.FC<ReviewPanelProps> = ({
  currentStatus,
  latestReview,
  onReview,
}) => {
  const [comments, setComments] = useState(latestReview?.comments ?? '');
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleActionClick = (action: ReviewAction) => {
    // Approve/reject need confirmation; revision_needed can go straight through
    if (action === 'approved' || action === 'rejected') {
      setPending({ action });
    } else {
      handleConfirm(action);
    }
  };

  const handleConfirm = async (action: ReviewAction) => {
    setPending(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onReview(action, comments);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Supervisor Review</h3>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TASK_STATUS_COLORS[currentStatus]}`}
        >
          {TASK_STATUS_LABELS[currentStatus]}
        </span>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Latest review comment */}
        {latestReview?.comments && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Previous comment
            </p>
            <p className="text-sm text-gray-700">{latestReview.comments}</p>
          </div>
        )}

        {/* Comment textarea */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Review Comment
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitting}
            rows={4}
            placeholder="Add a review comment (optional for approval, recommended for revision/rejection)..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-50"
          />
        </div>

        {/* Error */}
        {submitError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {submitError}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {(Object.keys(ACTION_CONFIG) as ReviewAction[]).map((action) => {
            const config = ACTION_CONFIG[action];
            return (
              <button
                key={action}
                type="button"
                onClick={() => handleActionClick(action)}
                disabled={isSubmitting}
                className={[
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
                  config.buttonClass,
                  isSubmitting ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  config.icon
                )}
                {config.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Confirmation overlay */}
      {pending && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl">
          <div className="mx-4 bg-white border border-gray-200 rounded-xl shadow-lg p-6 max-w-sm w-full">
            <p className="text-sm font-semibold text-gray-800 mb-2">Confirm Action</p>
            <p className="text-sm text-gray-600 mb-5">
              {ACTION_CONFIG[pending.action].confirmText}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="flex-1 px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirm(pending.action)}
                className={[
                  'flex-1 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
                  ACTION_CONFIG[pending.action].buttonClass,
                ].join(' ')}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;
